import React, { useState } from 'react';
import { format, addWeeks, subWeeks, isAfter, isSameDay } from 'date-fns';
import Layout from '../components/Layout';
import DailyHeader from '../components/DailyHeader';
import MoodSelector from '../components/MoodSelector';
import { useMood } from '../contexts/MoodContext';
import { getWeekDays, getCurrentTimeOfDay } from '../utils/dateUtils';
import { getMoodEmoji, getMoodGradient } from '../utils/moodUtils';

const WeeklyView: React.FC = () => {
  const { currentDate, setCurrentDate, weeklyMoods, saveMood } = useMood();
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<'morning' | 'afternoon' | 'evening' | null>(null);
  
  const weekDays = getWeekDays(currentDate);
  const today = new Date();
  const currentTimeOfDay = getCurrentTimeOfDay();
  
  const handlePreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };
  
  const handleNextWeek = () => {
    const nextWeek = addWeeks(currentDate, 1);
    // Allow navigation to next week but still prevent editing future moods
    setCurrentDate(nextWeek);
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleMoodSelect = (mood: 1 | 2 | 3 | 4 | 5) => {
    if (selectedDay && selectedTimeOfDay) {
      saveMood(mood, selectedTimeOfDay, undefined);
      setSelectedDay(null);
      setSelectedTimeOfDay(null);
    }
  };
  
  const isTimeSlotEditable = (day: Date, timeOfDay: 'morning' | 'afternoon' | 'evening'): boolean => {
    // Reset time to start of day for accurate comparison
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    
    // Future days are not editable
    if (dayStart.getTime() > todayStart.getTime()) {
      return false;
    }
    
    // Past days are always editable
    if (dayStart.getTime() < todayStart.getTime()) {
      return true;
    }
    
    // For today, check if the time period has passed or is current
    const timeOrder = { morning: 0, afternoon: 1, evening: 2 };
    const currentTimeOrder = timeOrder[currentTimeOfDay];
    const targetTimeOrder = timeOrder[timeOfDay];
    
    return targetTimeOrder <= currentTimeOrder;
  };
  
  const handleBoxClick = (day: Date, timeOfDay: 'morning' | 'afternoon' | 'evening') => {
    if (!isTimeSlotEditable(day, timeOfDay)) {
      return;
    }
    
    setCurrentDate(day);
    setSelectedDay(day);
    setSelectedTimeOfDay(timeOfDay);
  };
  
  const getBoxStyles = (day: Date, timeOfDay: 'morning' | 'afternoon' | 'evening', gradient: string) => {
    const isEditable = isTimeSlotEditable(day, timeOfDay);
    
    return `aspect-square flex items-center justify-center rounded-md text-4xl ${
      !isEditable 
        ? 'bg-gray-100 cursor-not-allowed opacity-50' 
        : `cursor-pointer transition-all hover:scale-105 ${
            gradient ? `bg-gradient-to-br ${gradient}` : 'bg-gray-50 hover:bg-gray-100'
          }`
    }`;
  };
  
  return (
    <Layout title="Weekly Overview">
      <DailyHeader
        date={currentDate}
        onPrevious={handlePreviousWeek}
        onNext={handleNextWeek}
        onToday={handleToday}
      />
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Weekly Overview</h3>
        
        <div className="grid grid-cols-8 gap-1 mb-4">
          <div className="text-sm font-medium text-gray-500"></div>
          {weekDays.map((day, index) => (
            <div key={index} className="text-sm font-medium text-gray-700 text-center">
              {format(day, 'EEE')}
              <div className="text-xs text-gray-500">{format(day, 'd')}</div>
            </div>
          ))}
          
          <div className="text-sm font-medium text-gray-500 flex items-center">Morning</div>
          {weekDays.map((day, index) => {
            const formattedDate = format(day, 'yyyy-MM-dd');
            const mood = weeklyMoods[formattedDate]?.morning;
            const gradient = mood ? getMoodGradient(mood.mood) : '';
            
            return (
              <div
                key={`morning-${index}`}
                className={getBoxStyles(day, 'morning', gradient)}
                onClick={() => handleBoxClick(day, 'morning')}
              >
                {mood ? getMoodEmoji(mood.mood) : '—'}
              </div>
            );
          })}
          
          <div className="text-sm font-medium text-gray-500 flex items-center">Afternoon</div>
          {weekDays.map((day, index) => {
            const formattedDate = format(day, 'yyyy-MM-dd');
            const mood = weeklyMoods[formattedDate]?.afternoon;
            const gradient = mood ? getMoodGradient(mood.mood) : '';
            
            return (
              <div
                key={`afternoon-${index}`}
                className={getBoxStyles(day, 'afternoon', gradient)}
                onClick={() => handleBoxClick(day, 'afternoon')}
              >
                {mood ? getMoodEmoji(mood.mood) : '—'}
              </div>
            );
          })}
          
          <div className="text-sm font-medium text-gray-500 flex items-center">Evening</div>
          {weekDays.map((day, index) => {
            const formattedDate = format(day, 'yyyy-MM-dd');
            const mood = weeklyMoods[formattedDate]?.evening;
            const gradient = mood ? getMoodGradient(mood.mood) : '';
            
            return (
              <div
                key={`evening-${index}`}
                className={getBoxStyles(day, 'evening', gradient)}
                onClick={() => handleBoxClick(day, 'evening')}
              >
                {mood ? getMoodEmoji(mood.mood) : '—'}
              </div>
            );
          })}
        </div>
        
        <div className="space-y-4">
          {weekDays.map(day => {
            const formattedDate = format(day, 'yyyy-MM-dd');
            const dailyMood = weeklyMoods[formattedDate];
            
            if (dailyMood?.reflection?.completed) {
              return (
                <div key={formattedDate} className="border-t border-gray-100 pt-3">
                  <div className="flex items-center gap-2 text-purple-600 mb-1">
                    <span className="text-sm font-medium">
                      {format(day, 'EEEE, MMMM d')} Reflection
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {dailyMood.reflection.notes || 'No notes added'}
                  </p>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
      
      {selectedDay && selectedTimeOfDay && (
        <MoodSelector
          timeOfDay={selectedTimeOfDay}
          date={selectedDay}
          onSelect={handleMoodSelect}
          onClose={() => {
            setSelectedDay(null);
            setSelectedTimeOfDay(null);
          }}
        />
      )}
    </Layout>
  );
};

export default WeeklyView;